import { ApexOptions } from 'apexcharts';
import { get } from 'lodash';
import { useCallback, useId, useMemo, useState } from 'react';
import { renderToString } from 'react-dom/server';
import { Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { REGISTER_ID_SEARCH_PARAM_KEY } from '@/modules/common/constants/search-params.ts';
import BalancePieChart from '@/modules/issuer-dashboard/components/BalancePieChart.tsx';
import HolderSearch from '@/modules/issuer-dashboard/components/HolderSearch.tsx';
import { useIssuerRegisterClassListQuery } from '@/modules/issuer-dashboard/queries/registers.tsx';
import { useTopHoldersQuery } from '@/modules/issuer-dashboard/queries/shareholders.tsx';
import { issuerDashboardPaths } from '@/modules/issuer-dashboard/routes.tsx';
import { InfoCard, Select, Spinner } from 'ui';
import { goodFormatNumber, goodFormatUSDCurrency } from 'ui/utils';

interface TopHoldersProps {
  title?: string;
  subtitle?: boolean;
  holderSearch?: boolean;
}

interface TopHoldersChart {
  labels: string[];
  series: number[];
  percentages: number[];
}

const TopHoldersChart = ({
  title = 'Holders',
  subtitle = false,
  holderSearch = false,
}: TopHoldersProps) => {
  const [params] = useSearchParams();
  const { pathname } = useLocation();
  const containerId = useId();
  const [holderById, setHolderById] = useState<number>();

  const currentRegisterId = +(params.get(REGISTER_ID_SEARCH_PARAM_KEY) || '0');
  const [registerClassFilter, setRegisterClassFilter] = useState<number | null>();

  const { data: dataTopHolders, isLoading: isTopHoldersLoading } = useTopHoldersQuery(
    { register_class_id: registerClassFilter || undefined },
    false,
  );

  const { data: classesData, isLoading: isLoadingClasses } = useIssuerRegisterClassListQuery(
    {},
    currentRegisterId,
  );

  const classesOptions = useMemo(
    () => [
      { label: 'All classes', value: 0 },
      ...(classesData?.registers || []).map(({ id, class_name }) => ({
        label: class_name,
        value: id,
      })),
    ],
    [classesData?.registers],
  );

  const { series, labels, percentages } = useMemo<TopHoldersChart>(
    () =>
      (dataTopHolders?.data?.data || []).reduce<TopHoldersChart>(
        ({ series, labels, percentages }, { balance, entity_name, percentage }) => ({
          series: [...series, balance],
          labels: [...labels, entity_name],
          percentages: [...percentages, percentage],
        }),
        { series: [], labels: [], percentages: [] },
      ),
    [dataTopHolders?.data?.data],
  );

  const addLegendTooltips = useCallback(
    (_: unknown, chartOptions: { config: ApexOptions }) => {
      const legendOptions = document.querySelectorAll(
        `#${CSS.escape(containerId)} .apexcharts-legend-series`,
      );

      const { series, labels } = chartOptions.config;

      if (!series || !labels) return;

      legendOptions.forEach(option => {
        const rel = option.getAttribute('rel');
        if (!rel || !+rel || typeof series?.[+rel - 1] === 'undefined') return;
        const balance = series[+rel - 1];
        const name = labels[+rel - 1];

        const tooltipElement = document.createElement('div');

        tooltipElement.textContent = `${name}: ${goodFormatUSDCurrency(balance as number)}`;

        tooltipElement.classList.add('absolute');
        tooltipElement.classList.add('hidden');
        tooltipElement.classList.add('group-hover:block');
        tooltipElement.classList.add('bg-white');
        tooltipElement.classList.add('border');
        tooltipElement.classList.add('rounded');
        tooltipElement.classList.add('px-2');
        tooltipElement.classList.add('py-1');
        tooltipElement.classList.add('z-10');
        tooltipElement.classList.add('right-0');
        tooltipElement.classList.add('max-w-[65%]');
        tooltipElement.classList.add('cursor-default');
        tooltipElement.classList.add('break-words');
        tooltipElement.classList.add('will-change-transform');

        if (+rel !== series.length) {
          tooltipElement.classList.add('bottom-[-30px]');
        } else {
          tooltipElement.classList.add('top-[-30px]');
        }

        option.classList.add('relative');
        option.classList.add('group');
        option.append(tooltipElement);
      });
    },
    [containerId],
  );

  const handleLegendClick = useCallback((entityId: number) => {
    setHolderById(entityId);
  }, []);

  const options = useMemo<ApexOptions>(
    () => ({
      chart: {
        events: {
          mounted: addLegendTooltips,
          updated: addLegendTooltips,
          legendClick: (_, index) => {
            const entityId = get(dataTopHolders?.data, `data.${index}.entity_id`);
            if (entityId) {
              handleLegendClick(entityId);
            }
          },
        },
      },
      responsive: [
        {
          breakpoint: 768,
          options: { plotOptions: { pie: { offsetY: 0 } } },
        },
      ],
      legend: {
        offsetY: -20,
        offsetX: -50,
        formatter: (label: string, { seriesIndex }) => {
          return renderToString(
            <div className="w-full flex justify-between">
              <div className="w-full overflow-hidden text-nowrap text-ellipsis">{label}</div>
              <div className="font-semibold">{goodFormatNumber(percentages[seriesIndex])}%</div>
            </div>,
          );
        },
      },
      plotOptions: { pie: { offsetY: 50 } },
    }),
    [addLegendTooltips, dataTopHolders?.data, handleLegendClick, percentages],
  );

  if (holderById) {
    return (
      <Navigate
        to={{
          pathname: issuerDashboardPaths.dynamic.holder(holderById.toString()),
          search: params.toString(),
        }}
        state={{ from: pathname + '?' + params.toString() }}
      />
    );
  }

  return (
    <InfoCard title={title}>
      <div className="flex flex-col [&_.apexcharts-legend]:!overflow-auto" id={containerId}>
        {holderSearch && (
          <>
            <p className="text-sm font-semibold mb-4">Find holder by name or ID</p>
            <div className="mb-6">
              <HolderSearch />
            </div>
          </>
        )}

        {subtitle && <span className="text-sm font-semibold mb-3 md:mb-0">Top holders</span>}
        <div className="relative">
          <Select
            isClearable={!!registerClassFilter}
            label=""
            placeholder="All classes"
            classNameLabel="!mb-0"
            className="md:absolute w-full md:w-1/2 md:top-[7px] h-10 z-[2]"
            onChange={setRegisterClassFilter}
            value={registerClassFilter || 0}
            options={classesOptions}
            withBorder
          />
          {(isLoadingClasses || isTopHoldersLoading) && (
            <div className="p-4 w-full h-[440.69px] xl:h-[267px] flex items-center justify-center">
              <Spinner size="lg" />
            </div>
          )}
          {!isLoadingClasses && !isTopHoldersLoading && (
            <BalancePieChart options={options} series={series} labels={labels} />
          )}
        </div>
      </div>
    </InfoCard>
  );
};
export default TopHoldersChart;
